import { useEffect, useRef, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { FolderOpen } from 'lucide-react'
import {
  useCompanyDocuments,
  useUploadCompanyDocument,
  useDeleteCompanyDocument,
} from '@/hooks/use-api'
import { apiClient } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import type { CompanyDocument } from '@/types/hr'

function formatBytes(n?: number | null): string {
  if (!n) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/** Company-wide HR documents (contract/offer templates, the Employee Handbook,
 * agreements, Health & Safety manuals). Admin + Super Admin; bytes in GCS,
 * streamed through a gated endpoint. */
export default function CompanyDocumentsPage() {
  const { toast } = useToast()
  const { data, isLoading, error } = useCompanyDocuments()
  const uploadDoc = useUploadCompanyDocument()
  const deleteDoc = useDeleteCompanyDocument()
  const fileRef = useRef<HTMLInputElement>(null)

  const docs = data ?? []
  const groups = new Map<string, CompanyDocument[]>()
  for (const d of docs) {
    if (!groups.has(d.document_type)) groups.set(d.document_type, [])
    groups.get(d.document_type)!.push(d)
  }
  const categories = [...groups.keys()].sort()

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const fd = new FormData()
    fd.append('file', file)
    try {
      await uploadDoc.mutateAsync({ formData: fd })
      toast({ title: 'Document uploaded' })
    } catch (err) {
      toast({
        title: 'Upload failed',
        description:
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
          'Could not upload the file.',
        variant: 'destructive',
      })
    }
  }

  const download = async (docId: string, name: string) => {
    try {
      const blob = await apiClient.downloadCompanyDocument(docId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = name
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast({ title: 'Download failed', variant: 'destructive' })
    }
  }

  // In-app preview: fetch the (auth-gated) bytes, type the blob so the browser
  // renders it inline, and show it in a modal. Object URLs revoked on close.
  const [viewer, setViewer] = useState<{ doc: CompanyDocument; url: string } | null>(null)
  const [viewerLoadingId, setViewerLoadingId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<CompanyDocument | null>(null)

  const closeViewer = () => {
    setViewer((prev) => {
      if (prev) URL.revokeObjectURL(prev.url)
      return null
    })
  }

  const openViewer = async (doc: CompanyDocument) => {
    setViewerLoadingId(doc.id)
    try {
      const raw = await apiClient.downloadCompanyDocument(doc.id)
      const typed = new Blob([raw], { type: doc.mime_type || 'application/octet-stream' })
      const url = URL.createObjectURL(typed)
      setViewer((prev) => {
        if (prev) URL.revokeObjectURL(prev.url)
        return { doc, url }
      })
    } catch {
      toast({ title: 'Could not open document', variant: 'destructive' })
    } finally {
      setViewerLoadingId(null)
    }
  }

  useEffect(() => {
    return () => {
      setViewer((prev) => {
        if (prev) URL.revokeObjectURL(prev.url)
        return null
      })
    }
  }, [])

  const mime = viewer?.doc.mime_type ?? ''
  const isPdf = mime === 'application/pdf' || /\.pdf$/i.test(viewer?.doc.name ?? '')
  const isImage = mime.startsWith('image/')
  const isText = mime.startsWith('text/')
  const canPreview = isPdf || isImage || isText

  return (
    <main className="container mx-auto max-w-5xl p-6">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <FolderOpen className="h-6 w-6" aria-hidden="true" />
            Company Documents
          </h1>
          <p className="text-sm text-muted-foreground">
            Generic GrackleDocs Inc. HR documents — templates, handbook, agreements and Health &amp;
            Safety. Available to Admins.
          </p>
        </div>
        <div>
          <input
            ref={fileRef}
            type="file"
            className="sr-only"
            aria-hidden="true"
            tabIndex={-1}
            onChange={onFile}
          />
          <Button onClick={() => fileRef.current?.click()} disabled={uploadDoc.isPending}>
            {uploadDoc.isPending ? 'Uploading…' : 'Upload document'}
          </Button>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">All Documents</CardTitle>
          <CardDescription>Stored securely and streamed on demand.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <p role="status" aria-live="polite" className="text-sm text-muted-foreground">
              Loading…
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-destructive">
              Failed to load documents.
            </p>
          )}
          {!isLoading && !error && docs.length === 0 && (
            <p className="text-sm text-muted-foreground">No documents yet.</p>
          )}
          {categories.map((cat) => (
            <div key={cat} className="mb-4">
              <h2 className="mb-2 mt-1 border-b-2 border-[#072744]/40 pb-1 text-sm font-bold uppercase tracking-wide text-[#072744]">
                {cat}
              </h2>
              <ul className="divide-y divide-border rounded-md border border-border">
                {groups.get(cat)!.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
                  >
                    <button
                      type="button"
                      onClick={() => openViewer(d)}
                      disabled={viewerLoadingId === d.id}
                      className="flex-1 truncate text-left text-primary underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
                      aria-label={`View ${d.name}`}
                    >
                      {d.name}
                      {viewerLoadingId === d.id ? (
                        <span className="ml-2 text-xs text-muted-foreground">Opening…</span>
                      ) : d.file_size ? (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {formatBytes(d.file_size)}
                        </span>
                      ) : null}
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => setDeleteTarget(d)}
                      aria-label={`Delete ${d.name}`}
                    >
                      Delete
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && closeViewer()}>
        <DialogContent className="flex max-h-[90vh] max-w-5xl flex-col">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">{viewer?.doc.name}</DialogTitle>
          </DialogHeader>
          <div className="h-[80vh] w-full overflow-auto rounded-md border border-border bg-muted/30">
            {viewer && canPreview ? (
              isImage ? (
                <div className="flex h-full w-full items-center justify-center p-2">
                  <img
                    src={viewer.url}
                    alt={viewer.doc.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <iframe
                  src={viewer.url}
                  title={viewer.doc.name}
                  className="h-full w-full border-0"
                />
              )
            ) : (
              <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Preview isn’t available for this file type. Download it to view.
                </p>
                {viewer && (
                  <Button size="sm" onClick={() => download(viewer.doc.id, viewer.doc.name)}>
                    Download
                  </Button>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:justify-between">
            <div className="flex gap-2">
              {viewer && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => download(viewer.doc.id, viewer.doc.name)}
                >
                  Download
                </Button>
              )}
              {viewer && canPreview && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(viewer.url, '_blank', 'noopener')}
                >
                  Open in new tab
                </Button>
              )}
            </div>
            <Button variant="secondary" size="sm" onClick={closeViewer}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              “{deleteTarget?.name}” will be removed from the company library. This can’t be undone
              from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteTarget) deleteDoc.mutate({ docId: deleteTarget.id })
                setDeleteTarget(null)
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  )
}
